import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Typography, IconButton, Chip, Button, Dialog, DialogTitle,
    DialogContent, DialogContentText, DialogActions, Avatar, Skeleton,
    Switch, Tooltip, TextField, MenuItem, InputAdornment, Drawer,
    Divider, Stack, Grid, Badge, Pagination
} from '@mui/material';
import { IoPersonAddSharp } from 'react-icons/io5';
import UserModal from '../../components/Users/UserModal';
import {
    MdDeleteForever, MdEdit, MdVisibility, MdSearch, MdFilterList,
    MdClear, MdPhone, MdEmail, MdBusiness, MdOutlineAdminPanelSettings
} from 'react-icons/md';
import { getData, postFileUpload, deleteData, putData } from '../../Webservice/Webservice';
import USER_ROUTES from '../../API_Route/UserRoute';

const roleColorMap = {
    super_admin: { bg: '#dcfce7', color: '#166534' },
    admin: { bg: '#dbeafe', color: '#1e40af' },
    hr: { bg: '#e0f2fe', color: '#075985' },
    product_manager: { bg: '#ede9fe', color: '#5b21b6' },
    order_manager: { bg: '#ffedd5', color: '#9a3412' },
    support_staff: { bg: '#d1fae5', color: '#065f46' },
    warehouse_admin: { bg: '#fce7f3', color: '#9d174d' },
    relationship_manager: { bg: '#e0f2fe', color: '#0369a1' },
    influencer: { bg: '#faf5ff', color: '#6b21a8' },
};

const getRoleColor = (roleName) => {
    const key = (roleName || '').toLowerCase().replace(/ /g, '_');
    return roleColorMap[key] || { bg: '#f3f4f6', color: '#4b5563' };
};

const getRoleLabel = (user) => {
    const name = user.role_id?.name || user.role || '';
    return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'N/A';
};

const PAGE_SIZE = 15;

const Usermangement = () => {
    const [open, setOpen] = useState(false);
    const [newUser, setNewUser] = useState({
        firstName: '', lastName: '', email: '', phoneNumber: '',
        role_id: '', password: '', status: 'active',
        permissions: [], warehouseId: '', image: '', createdBy: ''
    });
    const [userList, setUserList] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [allPermissionsList, setAllPermissionsList] = useState([]);
    const [roles, setRoles] = useState([]);

    // View drawer
    const [viewUser, setViewUser] = useState(null);

    // Filters
    const [searchText, setSearchText] = useState('');
    const [filterRole, setFilterRole] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    // Pagination
    const [page, setPage] = useState(1);

    const token = `Bearer ${sessionStorage.getItem('token') || ''}`;


    // ── Helper: is this user an admin-panel user? ──────────────────────
    // role_id is populated with { _id, name, roleFor } from the API
    const isAdminPanelUser = (user) => {
        const roleFor = user.role_id?.roleFor;
        const roleName = user.role_id?.name?.trim().toLowerCase();
        return roleFor === 'admin' || roleName === 'warehouse_admin';
    };

    // ── Fetch ──────────────────────────────────────────────────────────
    const fetchData = async () => {
        setLoading(true);
        try {
            const [users, rolesData] = await Promise.all([
                getData(USER_ROUTES.GET_USER, undefined, token),
                getData('user/role', undefined, token)
            ]);
            const fetchedRoles = Array.isArray(rolesData) ? rolesData : [];
            setRoles(fetchedRoles);

            // Filter using populated role_id.roleFor on each user directly
            const adminUsers = (users || []).filter(isAdminPanelUser);
            setUserList(adminUsers);
        } catch (e) {
            console.error('Failed to fetch data:', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []); // eslint-disable-line

    // ── Filtered + paginated list ──────────────────────────────────────
    const filteredUsers = useMemo(() => {
        const q = searchText.toLowerCase();
        return userList.filter(u => {
            const matchName = `${u.firstName} ${u.lastName}`.toLowerCase().includes(q);
            const matchEmail = (u.email || '').toLowerCase().includes(q);
            const matchPhone = (u.phoneNumber || '').includes(q);
            const matchSearch = !q || matchName || matchEmail || matchPhone;

            const roleName = u.role_id?.name || u.role || '';
            const matchRole = !filterRole || roleName.toLowerCase() === filterRole.toLowerCase();
            const matchStatus = !filterStatus || (u.status || '').toLowerCase() === filterStatus.toLowerCase();

            return matchSearch && matchRole && matchStatus;
        });
    }, [userList, searchText, filterRole, filterStatus]);

    const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
    const paginatedUsers = filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    // Reset to page 1 when filters change
    useEffect(() => { setPage(1); }, [searchText, filterRole, filterStatus]);

    // ── Role options for filter dropdown ──────────────────────────────
    const roleOptions = useMemo(() => {
        const seen = new Set();
        userList.forEach(u => {
            const name = u.role_id?.name || u.role || '';
            if (name) seen.add(name);
        });
        return Array.from(seen);
    }, [userList]);

    // ── Helpers ────────────────────────────────────────────────────────
    const refreshList = async () => {
        const users = await getData(USER_ROUTES.GET_USER, undefined, token);
        const adminUsers = (users || []).filter(isAdminPanelUser);
        setUserList(adminUsers);
    };

    // ── Handlers ───────────────────────────────────────────────────────
    const handleOpen = () => {
        const userId = sessionStorage.getItem('userId');
        setNewUser({
            firstName: '', lastName: '', email: '', phoneNumber: '',
            role_id: '', password: '', status: 'active',
            permissions: [], warehouseId: '', image: '', createdBy: userId || ''
        });
        setEditingUser(null);
        setOpen(true);
    };

    const handleEditOpen = (user) => {
        setEditingUser(user);
        setNewUser({
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            phoneNumber: user.phoneNumber || '',
            role_id: user.role_id?._id || user.role_id || '',
            password: '',
            status: user.status || 'active',
            permissions: user.permissions || [],
            warehouseId: (user.warehouseId && typeof user.warehouseId === 'object')
                ? user.warehouseId._id : (user.warehouseId || ''),
            image: user.image || '',
            createdBy: user.createdBy || sessionStorage.getItem('userId') || ''
        });
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditingUser(null);
    };

    const handleChange = (e) => {
        let { name, value } = e.target;
        if (name === 'mobileNumber') name = 'phoneNumber';
        if (name === 'role') name = 'role_id';
        setNewUser(prev => ({ ...prev, [name]: value }));
    };

    const getPermissionIds = (selectedPermissions, permissionsList) => {
        const ids = [];
        permissionsList.forEach((perm) => {
            if (selectedPermissions.includes(perm.name) && perm._id) ids.push(perm._id);
            if (perm.submenus && Array.isArray(perm.submenus)) {
                perm.submenus.forEach((sub) => {
                    if (selectedPermissions.includes(perm.name + ' > ' + sub.name) && sub._id) {
                        ids.push(sub._id);
                    }
                });
            }
        });
        return ids;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const warehouseAdminRoleId = roles.find(r => r.name?.toLowerCase() === 'warehouse_admin')?._id;
        try {
            const formData = new FormData();
            formData.append('firstName', newUser.firstName);
            formData.append('lastName', newUser.lastName);
            formData.append('email', newUser.email);
            formData.append('phoneNumber', newUser.phoneNumber);
            formData.append('status', newUser.status);
            formData.append('createdBy', newUser.createdBy);
            formData.append('role_id', newUser.role_id);
            if (newUser.password) formData.append('password', newUser.password);

            if (newUser.role_id !== warehouseAdminRoleId && newUser.permissions?.length) {
                const ids = getPermissionIds(newUser.permissions, allPermissionsList);
                formData.append('permissions', JSON.stringify(ids));
            }
            if (newUser.role_id === warehouseAdminRoleId && newUser.warehouseId) {
                formData.append('warehouseId', newUser.warehouseId);
            }
            if (newUser.image instanceof File) {
                formData.append('image', newUser.image);
            } else if (typeof newUser.image === 'string' && newUser.image.startsWith('data:image')) {
                const arr = newUser.image.split(',');
                const mime = arr[0].match(/:(.*?);/)[1];
                const bstr = atob(arr[1]);
                let n = bstr.length;
                const u8arr = new Uint8Array(n);
                while (n--) u8arr[n] = bstr.charCodeAt(n);
                formData.append('image', new Blob([u8arr], { type: mime }), 'user_image.png');
            }

            if (editingUser?._id) {
                await putData(`user/update/${editingUser._id}`, formData, token);
            } else {
                await postFileUpload(USER_ROUTES.ADD_USER, formData, token);
            }
            await refreshList();
            handleClose();
        } catch (err) {
            console.error('Failed to save user:', err);
        }
    };

    // ── Quick Status Toggle ────────────────────────────────────────────
    const handleStatusToggle = async (user) => {
        const newStatus = user.status === 'active' ? 'inActive' : 'active';
        try {
            const formData = new FormData();
            formData.append('status', newStatus);
            await putData(`user/update/${user._id}`, formData, token);
            // Update view drawer immediately for responsiveness
            if (viewUser?._id === user._id) {
                setViewUser(prev => ({ ...prev, status: newStatus }));
            }
            // Refresh the full list from server
            await refreshList();
        } catch (e) {
            console.error('Status toggle failed:', e);
        }
    };

    // ── Delete ─────────────────────────────────────────────────────────
    const handleDeleteClick = (user) => { setUserToDelete(user); setDeleteDialogOpen(true); };
    const handleCloseDeleteDialog = () => { setDeleteDialogOpen(false); setUserToDelete(null); };

    const handleConfirmDelete = async () => {
        if (!userToDelete) return;
        try {
            await deleteData(`user/delete/${userToDelete._id || userToDelete.id}`, token);
            await refreshList();
        } catch (e) {
            console.error('Delete failed:', e);
        }
        setDeleteDialogOpen(false);
        setUserToDelete(null);
    };

    const clearFilters = () => { setSearchText(''); setFilterRole(''); setFilterStatus(''); };
    const hasActiveFilters = searchText || filterRole || filterStatus;

    // ─────────────────────────────────────────────────────────────────────
    if (open) {
        return (
            <Box mt={2}>
                <UserModal
                    open={true}
                    handleClose={handleClose}
                    newUser={newUser}
                    handleChange={handleChange}
                    handleSubmit={handleSubmit}
                    isEdit={!!editingUser}
                    editingUser={editingUser}
                    onPermissionsLoaded={setAllPermissionsList}
                    asPage={true}
                />
            </Box>
        );
    }

    return (
        <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', p: { xs: 1, md: 2 } }}>

            {/* ── Header ───────────────────────────────────────────────── */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 800, color: '#111827' }}>
                        User Management
                    </Typography>
                    <Typography sx={{ fontSize: '0.82rem', color: '#6b7280', fontFamily: 'Mulish, sans-serif' }}>
                        {filteredUsers.length} of {userList.length} admin users
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<IoPersonAddSharp />} onClick={handleOpen}
                    sx={{
                        fontFamily: 'Mulish, sans-serif', bgcolor: '#ffd102', color: '#000',
                        fontWeight: 700, textTransform: 'none', borderRadius: '10px', px: 3,
                        boxShadow: '0 4px 12px rgba(255,209,2,0.3)',
                        '&:hover': { bgcolor: '#f59e0b', boxShadow: '0 6px 16px rgba(255,209,2,0.4)' }
                    }}>
                    Create User
                </Button>
            </Box>

            {/* ── Filter Bar ───────────────────────────────────────────── */}
            <Box sx={{
                bgcolor: '#fff', borderRadius: 2, border: '1px solid #e5e7eb',
                boxShadow: '0 1px 4px rgba(0,0,0,0.05)', p: 2, mb: 2,
                display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center'
            }}>
                <TextField size="small" placeholder="Search name, email, phone..."
                    value={searchText} onChange={e => setSearchText(e.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><MdSearch color="#9ca3af" size={18} /></InputAdornment>,
                        endAdornment: searchText && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearchText('')}>
                                    <MdClear size={15} color="#9ca3af" />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                    sx={{ minWidth: 240, '& .MuiInputBase-root': { fontFamily: 'Mulish, sans-serif', fontSize: '0.88rem' } }} />

                <TextField select size="small" label="Role" value={filterRole}
                    onChange={e => setFilterRole(e.target.value)}
                    sx={{
                        minWidth: 180, '& .MuiInputBase-root': { fontFamily: 'Mulish, sans-serif', fontSize: '0.88rem' },
                        '& .MuiInputLabel-root': { fontFamily: 'Mulish, sans-serif' }
                    }}>
                    <MenuItem value="">All Roles</MenuItem>
                    {roleOptions.map(r => (
                        <MenuItem key={r} value={r} sx={{ fontFamily: 'Mulish, sans-serif', textTransform: 'capitalize' }}>
                            {r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField select size="small" label="Status" value={filterStatus}
                    onChange={e => setFilterStatus(e.target.value)}
                    sx={{
                        minWidth: 140, '& .MuiInputBase-root': { fontFamily: 'Mulish, sans-serif', fontSize: '0.88rem' },
                        '& .MuiInputLabel-root': { fontFamily: 'Mulish, sans-serif' }
                    }}>
                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inActive">Inactive</MenuItem>
                </TextField>

                {hasActiveFilters && (
                    <Button size="small" variant="outlined" startIcon={<MdClear size={14} />}
                        onClick={clearFilters}
                        sx={{
                            fontFamily: 'Mulish, sans-serif', textTransform: 'none', fontWeight: 600,
                            borderColor: '#e5e7eb', color: '#6b7280', borderRadius: '8px',
                            '&:hover': { bgcolor: '#f9fafb', borderColor: '#d1d5db' }
                        }}>
                        Clear Filters
                    </Button>
                )}

                <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                    {['active', 'inActive'].map(s => (
                        <Chip key={s} size="small"
                            label={`${s === 'active' ? '● Active' : '○ Inactive'}: ${userList.filter(u => u.status === s).length}`}
                            sx={{
                                fontFamily: 'Mulish, sans-serif', fontWeight: 700, fontSize: '0.72rem',
                                bgcolor: s === 'active' ? '#dcfce7' : '#fee2e2',
                                color: s === 'active' ? '#166534' : '#991b1b'
                            }} />
                    ))}
                </Box>
            </Box>

            {/* ── Table ────────────────────────────────────────────────── */}
            <Box sx={{
                bgcolor: '#fff', borderRadius: 2, border: '1px solid #e5e7eb',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden'
            }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{
                                '& th': {
                                    bgcolor: '#fafafa', fontFamily: 'Mulish, sans-serif',
                                    fontWeight: 800, fontSize: '0.72rem', textTransform: 'uppercase',
                                    letterSpacing: '0.05em', color: '#6b7280',
                                    borderBottom: '2px solid #e5e7eb', py: 1.5, whiteSpace: 'nowrap'
                                }
                            }}>
                                <TableCell>User</TableCell>
                                <TableCell>Contact</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell>Joined</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading
                                ? Array.from({ length: 6 }).map((_, i) => (
                                    <TableRow key={i}>
                                        {Array.from({ length: 6 }).map((_, j) => (
                                            <TableCell key={j}><Skeleton height={36} /></TableCell>
                                        ))}
                                    </TableRow>
                                ))
                                : paginatedUsers.length > 0
                                    ? paginatedUsers.map((user) => {
                                        const roleLabel = getRoleLabel(user);
                                        const roleColor = getRoleColor(user.role_id?.name || user.role);
                                        const isActive = user.status === 'active';
                                        const initials = `${(user.firstName || '')[0] || ''}${(user.lastName || '')[0] || ''}`.toUpperCase();

                                        return (
                                            <TableRow key={user._id || user.id} hover
                                                sx={{ '&:hover': { bgcolor: '#fffbeb' }, transition: 'background 0.15s' }}>

                                                {/* User */}
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                        <Badge
                                                            overlap="circular"
                                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                            badgeContent={
                                                                <Box sx={{
                                                                    width: 10, height: 10, borderRadius: '50%',
                                                                    bgcolor: isActive ? '#10b981' : '#ef4444',
                                                                    border: '2px solid #fff'
                                                                }} />
                                                            }>
                                                            <Avatar src={user.image} alt={user.firstName}
                                                                sx={{
                                                                    width: 40, height: 40, bgcolor: '#ffd102',
                                                                    color: '#000', fontWeight: 800,
                                                                    fontSize: '0.85rem', border: '2px solid #fff',
                                                                    boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                                                                }}>
                                                                {!user.image && initials}
                                                            </Avatar>
                                                        </Badge>
                                                        <Box>
                                                            <Typography sx={{
                                                                fontWeight: 700, fontFamily: 'Mulish, sans-serif',
                                                                fontSize: '0.9rem', color: '#111827'
                                                            }}>
                                                                {user.firstName} {user.lastName}
                                                            </Typography>
                                                            <Typography sx={{
                                                                fontSize: '0.72rem', color: '#9ca3af',
                                                                fontFamily: 'Mulish, sans-serif'
                                                            }}>
                                                                {user.empId ? `${user.empId}` : `#${(user._id || '').slice(-6).toUpperCase()}`}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>

                                                {/* Contact */}
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                                                            <MdEmail size={13} color="#9ca3af" />
                                                            <Typography sx={{
                                                                fontFamily: 'Mulish, sans-serif',
                                                                fontSize: '0.82rem', color: '#374151'
                                                            }}>
                                                                {user.email || '—'}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                                                            <MdPhone size={13} color="#9ca3af" />
                                                            <Typography sx={{
                                                                fontFamily: 'Mulish, sans-serif',
                                                                fontSize: '0.8rem', color: '#6b7280'
                                                            }}>
                                                                {user.phoneNumber || '—'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>

                                                {/* Role */}
                                                <TableCell>
                                                    <Chip label={roleLabel} size="small"
                                                        icon={<MdOutlineAdminPanelSettings size={13} style={{ color: roleColor.color }} />}
                                                        sx={{
                                                            fontFamily: 'Mulish, sans-serif', fontWeight: 700,
                                                            fontSize: '0.72rem', bgcolor: roleColor.bg,
                                                            color: roleColor.color, border: 'none',
                                                            '& .MuiChip-icon': { ml: 0.8 }
                                                        }} />
                                                </TableCell>

                                                {/* Status Toggle */}
                                                <TableCell align="center">
                                                    <Tooltip title={`Click to mark ${isActive ? 'Inactive' : 'Active'}`}>
                                                        <Box sx={{
                                                            display: 'inline-flex', flexDirection: 'column',
                                                            alignItems: 'center', gap: 0.3
                                                        }}>
                                                            <Switch
                                                                checked={isActive}
                                                                onChange={() => handleStatusToggle(user)}
                                                                size="small"
                                                                sx={{
                                                                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#10b981' },
                                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#10b981' },
                                                                    '& .MuiSwitch-track': { bgcolor: '#ef4444' },
                                                                    '& .MuiSwitch-switchBase': { color: '#ef4444' },
                                                                }} />
                                                            <Typography sx={{
                                                                fontSize: '0.65rem', fontFamily: 'Mulish, sans-serif',
                                                                fontWeight: 700, color: isActive ? '#059669' : '#dc2626'
                                                            }}>
                                                                {isActive ? 'Active' : 'Inactive'}
                                                            </Typography>
                                                        </Box>
                                                    </Tooltip>
                                                </TableCell>

                                                {/* Joined */}
                                                <TableCell>
                                                    <Typography sx={{
                                                        fontFamily: 'Mulish, sans-serif',
                                                        fontSize: '0.82rem', color: '#6b7280'
                                                    }}>
                                                        {user.createdAt
                                                            ? new Date(user.createdAt).toLocaleDateString('en-IN')
                                                            : '—'}
                                                    </Typography>
                                                </TableCell>

                                                {/* Actions */}
                                                <TableCell align="center">
                                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                        <Tooltip title="View Details">
                                                            <IconButton size="small"
                                                                onClick={() => setViewUser(user)}
                                                                sx={{
                                                                    bgcolor: '#eff6ff', color: '#3b82f6',
                                                                    borderRadius: '8px', p: '6px',
                                                                    '&:hover': { bgcolor: '#dbeafe' }
                                                                }}>
                                                                <MdVisibility size={17} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Edit User">
                                                            <IconButton size="small"
                                                                onClick={() => handleEditOpen(user)}
                                                                sx={{
                                                                    bgcolor: '#fefce8', color: '#ca8a04',
                                                                    borderRadius: '8px', p: '6px',
                                                                    '&:hover': { bgcolor: '#fef9c3' }
                                                                }}>
                                                                <MdEdit size={17} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete User">
                                                            <IconButton size="small"
                                                                onClick={() => handleDeleteClick(user)}
                                                                sx={{
                                                                    bgcolor: '#fef2f2', color: '#dc2626',
                                                                    borderRadius: '8px', p: '6px',
                                                                    '&:hover': { bgcolor: '#fee2e2' }
                                                                }}>
                                                                <MdDeleteForever size={18} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                    : (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                                                <img src="./img/nodata.png" alt="No users"
                                                    style={{ width: '140px', opacity: 0.5 }} />
                                                <Typography sx={{
                                                    fontFamily: 'Mulish, sans-serif',
                                                    color: '#9ca3af', mt: 1.5, fontSize: '0.95rem'
                                                }}>
                                                    {hasActiveFilters ? 'No users match your filters' : 'No users found'}
                                                </Typography>
                                                {hasActiveFilters && (
                                                    <Button size="small" onClick={clearFilters} sx={{
                                                        mt: 1,
                                                        fontFamily: 'Mulish, sans-serif', textTransform: 'none'
                                                    }}>
                                                        Clear filters
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )
                            }
                        </TableBody>
                    </Table>
                </TableContainer>

                {filteredUsers.length > PAGE_SIZE && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: '1px solid #e5e7eb' }}>
                        <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)}
                            shape="rounded" showFirstButton showLastButton
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    fontFamily: 'Mulish, sans-serif',
                                    '&.Mui-selected': { bgcolor: '#ffd102', color: '#000' }
                                }
                            }} />
                    </Box>
                )}
            </Box>

            {/* ═══ VIEW DRAWER ══════════════════════════════════════════ */}
            <Drawer anchor="right" open={!!viewUser} onClose={() => setViewUser(null)}
                PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, p: 0 } }}>
                {viewUser && (
                    <>
                        {/* Header */}
                        <Box sx={{ bgcolor: '#111827', p: 3, pb: 4 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                <IconButton onClick={() => setViewUser(null)}
                                    sx={{ color: '#9ca3af', '&:hover': { color: '#fff' } }}>
                                    <MdClear size={20} />
                                </IconButton>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                                <Avatar src={viewUser.image} alt={viewUser.firstName}
                                    sx={{
                                        width: 80, height: 80, bgcolor: '#ffd102', color: '#000',
                                        fontWeight: 900, fontSize: '1.8rem',
                                        border: '4px solid rgba(255,255,255,0.15)'
                                    }}>
                                    {!viewUser.image && `${(viewUser.firstName || '')[0]}${(viewUser.lastName || '')[0]}`.toUpperCase()}
                                </Avatar>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography sx={{
                                        fontFamily: 'Mulish, sans-serif', fontWeight: 800,
                                        color: '#fff', fontSize: '1.2rem'
                                    }}>
                                        {viewUser.firstName} {viewUser.lastName}
                                    </Typography>
                                    <Chip label={getRoleLabel(viewUser)} size="small"
                                        sx={{
                                            mt: 0.5, fontFamily: 'Mulish, sans-serif', fontWeight: 700,
                                            fontSize: '0.72rem', bgcolor: 'rgba(255,209,2,0.15)',
                                            color: '#ffd102'
                                        }} />
                                </Box>
                                {/* Inline status toggle in drawer */}
                                <Box sx={{
                                    display: 'flex', alignItems: 'center', gap: 1,
                                    bgcolor: 'rgba(255,255,255,0.08)', px: 2, py: 1, borderRadius: 2
                                }}>
                                    <Typography sx={{
                                        fontFamily: 'Mulish, sans-serif', fontSize: '0.82rem',
                                        color: '#9ca3af'
                                    }}>Status:</Typography>
                                    <Switch checked={viewUser.status === 'active'}
                                        onChange={() => handleStatusToggle(viewUser)}
                                        size="small"
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#10b981' },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#10b981' }
                                        }} />
                                    <Typography sx={{
                                        fontFamily: 'Mulish, sans-serif', fontWeight: 700,
                                        fontSize: '0.82rem', color: viewUser.status === 'active' ? '#10b981' : '#ef4444'
                                    }}>
                                        {viewUser.status === 'active' ? 'Active' : 'Inactive'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Details */}
                        <Box sx={{ p: 3, overflowY: 'auto' }}>
                            {[
                                { label: 'Email Address', value: viewUser.email, icon: <MdEmail size={16} /> },
                                { label: 'Phone Number', value: viewUser.phoneNumber, icon: <MdPhone size={16} /> },
                                { label: 'Employee ID', value: viewUser.empId ? viewUser.empId : `#${(viewUser._id || '').slice(-8).toUpperCase()}`, icon: <MdOutlineAdminPanelSettings size={16} /> },
                                { label: 'Business', value: viewUser.business_name || '—', icon: <MdBusiness size={16} /> },
                                { label: 'Joined', value: viewUser.createdAt ? new Date(viewUser.createdAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' }) : '—', icon: null },
                                { label: 'Last Updated', value: viewUser.updatedAt ? new Date(viewUser.updatedAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' }) : '—', icon: null },
                            ].map((row, i) => (
                                <Box key={i} sx={{
                                    display: 'flex', justifyContent: 'space-between',
                                    alignItems: 'center', py: 1.5,
                                    borderBottom: i < 5 ? '1px solid #f3f4f6' : 'none'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {row.icon && <Box sx={{ color: '#9ca3af' }}>{row.icon}</Box>}
                                        <Typography sx={{
                                            fontFamily: 'Mulish, sans-serif',
                                            fontSize: '0.82rem', color: '#6b7280'
                                        }}>
                                            {row.label}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{
                                        fontFamily: 'Mulish, sans-serif',
                                        fontSize: '0.85rem', fontWeight: 600, color: '#111827',
                                        maxWidth: '55%', textAlign: 'right', wordBreak: 'break-word'
                                    }}>
                                        {row.value || '—'}
                                    </Typography>
                                </Box>
                            ))}

                            <Divider sx={{ my: 2 }} />

                            <Stack direction="row" spacing={1.5}>
                                <Button fullWidth variant="outlined"
                                    startIcon={<MdEdit size={16} />}
                                    onClick={() => { setViewUser(null); handleEditOpen(viewUser); }}
                                    sx={{
                                        fontFamily: 'Mulish, sans-serif', textTransform: 'none',
                                        fontWeight: 700, borderRadius: '10px',
                                        borderColor: '#ffd102', color: '#92400e',
                                        '&:hover': { bgcolor: '#fffbeb', borderColor: '#f59e0b' }
                                    }}>
                                    Edit
                                </Button>
                                <Button fullWidth variant="outlined"
                                    startIcon={<MdDeleteForever size={16} />}
                                    onClick={() => { setViewUser(null); handleDeleteClick(viewUser); }}
                                    sx={{
                                        fontFamily: 'Mulish, sans-serif', textTransform: 'none',
                                        fontWeight: 700, borderRadius: '10px',
                                        borderColor: '#fca5a5', color: '#dc2626',
                                        '&:hover': { bgcolor: '#fef2f2', borderColor: '#ef4444' }
                                    }}>
                                    Delete
                                </Button>
                            </Stack>
                        </Box>
                    </>
                )}
            </Drawer>

            {/* ═══ DELETE DIALOG ════════════════════════════════════════ */}
            <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}
                PaperProps={{ sx: { borderRadius: '14px', p: 2, minWidth: 380 } }}>
                <DialogTitle sx={{
                    fontSize: '1.1rem', fontWeight: 800, textAlign: 'center',
                    fontFamily: 'Mulish, sans-serif'
                }}>
                    <img style={{ width: '70px' }} src='./img/del1.png' alt='delete' />
                    <Typography sx={{
                        fontFamily: 'Mulish, sans-serif', fontWeight: 800,
                        fontSize: '1.1rem', mt: 1
                    }}>
                        Delete User
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{
                        textAlign: 'center', fontFamily: 'Mulish, sans-serif',
                        fontSize: '0.95rem', mb: 2, color: '#4b5563'
                    }}>
                        Are you sure you want to delete <strong>{userToDelete?.firstName} {userToDelete?.lastName}</strong>?
                        They won't be able to access the system.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', gap: 1.5, pb: 2 }}>
                    <Button onClick={handleCloseDeleteDialog} variant="outlined"
                        sx={{
                            minWidth: 120, fontFamily: 'Mulish, sans-serif', textTransform: 'none',
                            fontWeight: 600, borderColor: '#e5e7eb', color: '#6b7280',
                            '&:hover': { borderColor: '#d1d5db', bgcolor: '#f9fafb' }
                        }}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} variant="contained"
                        sx={{
                            minWidth: 120, fontFamily: 'Mulish, sans-serif', textTransform: 'none',
                            fontWeight: 700, bgcolor: '#dc2626',
                            '&:hover': { bgcolor: '#b91c1c' }
                        }}>
                        Yes, Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Usermangement;